import React, {useEffect, useState} from 'react';
import { ChevronRight, Check, AlertCircle, X, Calendar, Users, Coins } from 'lucide-react';
import OneTimeMessageCreator from "./OneTimeMessageCreator";
import OneTimeMessageSelection from "./OneTimeMessageSelection";
import OneTimeMessageSegment from "./OneTimeMessageSegment";
import {getVolatilityMessage, saveMessage, testSend, getMessagePrices} from "../../api/message/volatility-message.api";
import {collectMessageUrlErrors} from "../../libs/urlValidator";
import {wideListItemLabel} from "../../libs/helper";
import {useSearchParams} from "react-router-dom";
import TestSendModal from "../TestSendModal";

/**
 * 메시지 내용 필수 항목 검증 — 통과하면 null, 실패하면 `{ field, message }`.
 *
 * `field` 는 편집 화면에서 **그 입력칸을 빨갛게 강조**하기 위한 키다(`highlightField` prop).
 * 안내 문구만으로는 어느 칸인지 찾기 어렵다는 피드백(2026-07-29)에 따라 추가했다.
 *
 * 컴포넌트 밖의 **순수 함수**로 둔 이유: 단계 이동 검사(step 2)와 테스트 발송·예약 확정 시점 검사가
 * 같은 규칙을 써야 하기 때문이다. 예전에는 이 로직이 useEffect 안에만 있었고
 * step 3 에서는 무조건 `isValid = true` 가 되어, **테스트 발송은 사실상 무검증**이었다.
 * 그 결과 빈 항목이 그대로 발송 서버로 넘어가 400 → redirect 500 → 화면 튕김이 났다
 * (2026-07-29 와이드 리스트형 인시던트).
 *
 * `hooks` 는 기존 useEffect 가 검증 도중에 하던 부수효과(쿠폰 버튼 감지)를 그대로 유지하기 위한 것으로,
 * 테스트 발송 검증에서는 넘기지 않는다(화면 상태를 건드리면 안 되므로).
 */
export const validateMessageContent = (msgType, creatorData, hooks = {}) => {
    const fail = (field, message) => ({ field, message });

    if (!creatorData) return fail(null, "메시지 내용을 입력해주세요.");

    const validateButtons = (buttons, keyPrefix = "") => {
        const existsCoupon = !!buttons?.find(b => b.type === 'COUPON')
        hooks.onCouponPresence?.(existsCoupon)
        if (!buttons || buttons.length === 0) return null;

        if (existsCoupon) hooks.onCouponButtons?.()
        for (const [idx, btn] of buttons.entries()) {
            const key = `${keyPrefix}button.${idx}`;
            if (!btn.name || btn.name.trim() === '') {
                return fail(`${key}.name`, btn.type === 'COUPON'
                    ? "쿠폰명을 입력해주세요."
                    : `${idx + 1}번째 버튼의 이름을 입력해주세요.`);
            }
            if (btn.type === 'LINK') {
                if (!btn.urlMobile || !/^https?:\/\/\S+/.test(btn.urlMobile)) {
                    return fail(`${key}.urlMobile`, `${idx + 1}번째 버튼 URL은 http:// 또는 https://로 시작해야 하며 공백이 없어야 합니다.`);
                }
            } else if (btn.type === 'COUPON') {
                if (!btn.description || btn.description.trim() === '') {
                    return fail(`${key}.description`, "쿠폰 설명을 입력해주세요.");
                }
                // 첨부한 혜택(benefitId)도 링크(urlMobile)도 없으면 발송 시점에
                // `addCoupon` 이 NoSuchElementException("Url is empty") 를 던져 500 이 된다.
                if (!btn.benefitId && !(btn.urlMobile?.length > 0)) {
                    return fail(`${key}.name`, "쿠폰/혜택 버튼에 첨부할 혜택을 선택해주세요.");
                }
            }
        }
        return null;
    };

    switch (msgType) {
        case 'FI':
            if (!creatorData.content || creatorData.content.trim() === '') return fail("content", "메시지 내용을 입력해주세요.");
            return validateButtons(creatorData.buttons);

        case 'FW':
            if (!creatorData.image) return fail("image", "이미지를 업로드해주세요.");
            if (!creatorData.content || creatorData.content.trim() === '') return fail("content", "메시지 내용을 입력해주세요.");
            if (!creatorData.buttons || creatorData.buttons.length === 0) return fail("button.add", "최소 1개 이상의 버튼을 추가해주세요.");
            return validateButtons(creatorData.buttons);

        case 'FL': {
            if (!creatorData.header || creatorData.header.trim() === '') return fail("header", "리스트 타이틀을 입력해주세요.");
            if (!creatorData.buttons || creatorData.buttons.length === 0) return fail("button.add", "최소 1개 이상의 버튼을 추가해주세요.");
            const items = [...(creatorData.items ?? [])];
            if (items.length < 3) return fail("item.add", "리스트 항목을 3개 이상 입력해주세요.");
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const label = wideListItemLabel(i);
                if (!item?.imgUrl) return fail(`item.${i}.imgUrl`, `${label}의 이미지를 업로드해주세요.`);
                // 발송 서버가 아이템 내용을 필수(non-null)로 받는다. 예전엔 이 검사가 없어
                // 메인 아이템만 비워도 그대로 발송 요청이 나갔다 → 400 → 화면 튕김.
                if (!item?.title || item.title.trim() === '') return fail(`item.${i}.title`, `${label}의 내용을 입력해주세요.`);
                if (!item?.urlMobile || !/^https?:\/\/\S+/.test(item.urlMobile)) {
                    // 기본값이 'https://' 라서 그대로 두면 백엔드 URL 검증에서 400 이 난다.
                    return fail(`item.${i}.urlMobile`, `${label}의 링크는 http:// 또는 https://로 시작하는 완전한 주소여야 합니다.`);
                }
            }
            return validateButtons(creatorData.buttons);
        }

        case 'FC': {
            if (!creatorData.carousels || creatorData.carousels.length < 2) return fail("carousel.add", "캐러셀 피드는 최소 2개 이상이어야 합니다.");
            for (const [index, feed] of creatorData.carousels.entries()) {
                const attachment = feed.attachment
                const key = `carousel.${index}`;

                if (!attachment.image) return fail(`${key}.image`, `${index + 1}번째 피드의 이미지를 업로드해주세요.`);
                if (!feed.header || feed.header.trim() === '') return fail(`${key}.header`, `${index + 1}번째 피드의 타이틀을 입력해주세요.`);
                if (!feed.message || feed.message.trim() === '') return fail(`${key}.message`, `${index + 1}번째 피드의 내용을 입력해주세요.`);
                if (!attachment.buttons || attachment.buttons.length === 0) return fail(`${key}.button.add`, `${index + 1}번째 피드에 버튼을 최소 1개 추가해주세요.`);
                const btnError = validateButtons(attachment.buttons, `${key}.`);
                if (btnError) return fail(btnError.field, `${index + 1}번째 피드: ${btnError.message}`);
            }
            return null;
        }

        case 'FP':
            if (!creatorData.video || creatorData.video.videoUrl.trim() === '') return fail("video", "KAKAO TV Url을 입력해주세요.");
            return null;

        default:
            return null;
    }
};

/**
 * **비회원 친구(모먼트) 발송** 전용 추가 검증 — 통과하면 null, 실패하면 `{ field, message }`.
 *
 * 모먼트는 개인 발송과 완전히 다른 경로다. 백엔드가 카카오 모먼트 API 규격으로 소재를 **다시 만들며**
 * (`MessageData.toMomentData()`), 그 변환 코드가 `item.title!!`·`buttons!![0].urlMobile!!`·`image!!.imgUrl!!`
 * 처럼 non-null 단정을 쓴다 → 값이 비면 **예약 발송 시점에 NPE** 로 실패하고
 * 상태만 FAILED 가 된다(슬랙 알림만, 고객에게는 "예약했는데 안 갔다"로 보인다).
 *
 * 그래서 개인 발송 검증(validateMessageContent)보다 요구 항목이 많다.
 * 백엔드 `libs/MessageContentValidator.kt validateMomentContent()` 와 같은 규칙이어야 한다.
 */
export const validateMomentContent = (msgType, creatorData) => {
    const fail = (field, message) => ({ field, message });
    if (!creatorData) return fail(null, "메시지 내용을 입력해주세요.");

    // 이름이 있는 버튼은 모먼트에서 모바일 링크가 필수다.
    const buttonLinkError = (buttons, keyPrefix = "", label = "") => {
        const list = [...(buttons ?? [])];
        for (let i = 0; i < list.length; i++) {
            const btn = list[i];
            if (!btn?.name || btn.name.trim() === '') continue;
            if (!btn.urlMobile || !/^https?:\/\/\S+/.test(btn.urlMobile)) {
                return fail(`${keyPrefix}button.${i}.urlMobile`,
                    `${label}${i + 1}번째 버튼의 링크를 입력해주세요. 비회원 친구 발송에는 버튼 링크가 필수입니다.`);
            }
        }
        return null;
    };

    // 모먼트 소재에는 쿠폰(혜택)이 실리지 않는다 → 붙어 있으면 혜택 없는 메시지가 조용히 나간다.
    const hasCoupon = (creatorData.buttons ?? []).some(b => b?.type === 'COUPON')
        || (creatorData.carousels ?? []).some(f => (f?.attachment?.buttons ?? []).some(b => b?.type === 'COUPON'));
    if (hasCoupon) {
        return fail(null, "혜택(쿠폰)이 첨부된 메시지는 비회원 친구에게 발송할 수 없습니다. 회원 대상으로만 발송해 주세요.");
    }

    switch (msgType) {
        case 'FI':
            return buttonLinkError(creatorData.buttons);

        case 'FW': {
            // toMomentData FW 분기가 `buttons!![0].urlMobile!!`, `image!!.imgUrl!!` 를 무조건 읽는다.
            if (!creatorData.image?.imgUrl) return fail("image", "비회원 친구 발송에는 이미지가 필요합니다.");
            if (!creatorData.buttons || creatorData.buttons.length === 0) {
                return fail("button.add", "비회원 친구에게 보내려면 버튼이 1개 이상 필요합니다.");
            }
            return buttonLinkError(creatorData.buttons);
        }

        case 'FL': {
            // 아이템 검증은 validateMessageContent 가 이미 한다.
            // 여기서는 `buttons?.first()` 가 빈 배열에서 터지는 것만 추가로 막는다.
            if (creatorData.buttons && creatorData.buttons.length === 0) {
                return fail("button.add", "비회원 친구에게 보내려면 버튼이 1개 이상 필요합니다.");
            }
            return buttonLinkError(creatorData.buttons);
        }

        case 'FC': {
            const feeds = creatorData.carousels ?? [];
            for (let i = 0; i < feeds.length; i++) {
                const feedButtons = feeds[i]?.attachment?.buttons;
                if (!feedButtons || feedButtons.length === 0) {
                    return fail(`carousel.${i}.button.add`, `${i + 1}번째 피드에 버튼이 1개 이상 필요합니다(비회원 친구 발송).`);
                }
                const err = buttonLinkError(feedButtons, `carousel.${i}.`, `${i + 1}번째 피드 `);
                if (err) return err;
            }
            return null;
        }

        default:
            // toMomentData 가 FT/FI/FW/FL/FC 만 처리한다(나머지는 NoSuchElementException).
            return fail(null, "이 메시지 유형은 비회원 친구(모먼트) 발송을 지원하지 않습니다. 회원 대상으로만 발송해 주세요.");
    }
};

// --- [New] 커스텀 확인 모달 컴포넌트 ---
const ConfirmModal = ({ isOpen, onClose, onConfirm, data }) => {
    if (!isOpen) return null;

    const { totalCount, cost, reserveDate } = data;
    const formattedDate = reserveDate ? `${reserveDate.date} ${reserveDate.hour}:${reserveDate.min}` : '';

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                {/* Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800">메시지 예약 확인</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    <div className="flex items-center p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                        <Calendar size={24} className="text-indigo-600 mr-3" />
                        <div>
                            <div className="text-xs text-indigo-500 font-semibold uppercase">예약 발송 일시</div>
                            <div className="text-indigo-900 font-bold text-lg tracking-tight">{formattedDate}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center mb-1 text-gray-500 text-xs font-medium">
                                <Users size={14} className="mr-1" /> 총 발송 대상
                            </div>
                            <div className="text-gray-900 font-bold text-xl">{totalCount.toLocaleString()} <span className="text-sm font-normal text-gray-500">건</span></div>
                        </div>
                        <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                            <div className="flex items-center mb-1 text-yellow-600 text-xs font-medium">
                                <Coins size={14} className="mr-1" /> 예상 비용
                            </div>
                            <div className="text-yellow-700 font-bold text-xl">{cost.toLocaleString()} <span className="text-sm font-normal text-yellow-600">원</span></div>
                        </div>
                    </div>

                    <p className="text-center text-gray-500 text-xs mt-4">
                        위 내용으로 메시지 발송을 예약하시겠습니까?<br />
                        예약된 시간부터 순차적으로 발송됩니다.
                    </p>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                        취소
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 py-3 px-4 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-md transform transition active:scale-95"
                    >
                        예약하기
                    </button>
                </div>
            </div>
        </div>
    );
};

const OneTimeMessagePage = () => {
    const [query] = useSearchParams();
    const id = query.get("id");

    const [step, setStep] = useState(1);
    const [campaignName, setCampaignName] = useState("1회성 메시지 캠페인");
    // 불러온 메시지의 상태. 예약(RESERVED) 건을 열었을 때 상단에 알리기 위해서만 쓴다.
    // (목록에서 예약 건을 클릭하면 이 화면으로 온다, 2026-10-15. 종전에는 예약 건을 열 경로가 없었다.)
    const [loadedStatus, setLoadedStatus] = useState(null);
    const [msgType, setMsgType] = useState(null);

    const [creatorData, setCreatorData] = useState(null);

    // [Step 3] Segment 데이터 저장 (초기화 방지용)
    const [segmentData, setSegmentData] = useState(null);
    const [targetTargets, setTargetTargets] = useState({ member: true, nonMember: false });
    const [memberCount, setMemberCount] = useState(0);
    const [nonMemberCount, setNonMemberCount] = useState(0);

    // [New] 유효성 검사 상태
    const [isValid, setIsValid] = useState(false);
    const [validationMsg, setValidationMsg] = useState("");
    // 어느 입력칸이 문제인지 — 편집 화면에서 해당 칸을 빨갛게 강조한다(validateMessageContent 의 field).
    const [invalidField, setInvalidField] = useState(null);

    // [New] 모달 상태
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const totalCount = memberCount + nonMemberCount;
    // 단가는 서버(정가표)에서 받는다. 하드코딩 금지 — 위 주석 참조.
    const [messagePrices, setMessagePrices] = useState({});
    const [cost, setCost] = useState(0);

    const [isTestModalOpen, setIsTestModalOpen] = useState(false);

    const [isNotSendMoment, setIsNotSendMoment] = useState(false);

    /** 검증 실패 항목을 강조하고 '메시지 작성' 단계로 되돌린다. 강조 표시가 보이는 단계가 step 2 뿐이다. */
    const goBackToContentStep = (error) => {
        setValidationMsg(error.message);
        setInvalidField(error.field);
        setIsValid(false);
        setStep(2);
    };

    const handleOpenTestModal = () => {
        if (!creatorData) return alert("먼저 메시지를 작성해주세요.");
        // 테스트 발송도 실제 카카오 발송이라 필수 항목이 비면 발송 서버가 요청을 거절한다.
        // step 3 에서는 isValid 가 무조건 true 가 되므로 여기서 다시 검증해 사유를 알려준다.
        const error = validateMessageContent(msgType, creatorData);
        if (error) {
            // 문제 칸을 강조한 채 '메시지 작성' 단계로 되돌린다(step 3 에 머무르면 강조가 보이지 않는다).
            goBackToContentStep(error);
            return alert(`필수 항목을 모두 입력해야 테스트 발송할 수 있습니다.\n\n· ${error.message}`);
        }
        setIsTestModalOpen(true);
    };

    const executeTestSend = (selectedUsers) => {
        testSend(selectedUsers, false, transformToMessageData())
            .then((res) => {
                // 응답을 확인하지 않고 성공 안내를 띄우면 발송 실패인데 "발송했습니다"가 뜬다.
                // (백엔드 검증 400 은 fetch.js 가 사유를 alert 하고 여기까지 오지 않는다.)
                if (res?.code !== 2000) {
                    alert("테스트 발송에 실패했습니다. 메시지 내용을 확인한 뒤 다시 시도해 주세요.");
                    return;
                }
                alert(`${selectedUsers.length}명에게 테스트 메시지를 발송했습니다.`);
            })
        setIsTestModalOpen(false);
    };

    // --- [New] 메시지 타입 변경 핸들러 (초기화 경고) ---
    const handleTypeSelect = (newType) => {
        // 이미 선택된 타입과 같으면 무시
        if (msgType === newType) return;

        // 작성 중인 데이터가 있다면 경고
        if (creatorData) {
            if (window.confirm("메시지 타입을 변경하면 작성된 모든 내용이 초기화됩니다.\n변경하시겠습니까?")) {
                setMsgType(newType);
                setCreatorData(null); // 데이터 초기화
                setIsValid(false);    // 유효성 상태 초기화
                setValidationMsg(""); // 에러 메시지 초기화
            }
        } else {
            // 작성 중인 데이터가 없으면 바로 변경
            setMsgType(newType);
        }
    }

    useEffect(() => {
        if (id) {
            getVolatilityMessage(id).then(({data}) => {
                setLoadedStatus(data.status ?? null);
                setCampaignName(data.campaignName);
                setMsgType(data.messageData.type)
                setCreatorData(data.messageData)
                setSegmentData({ selectedFilters: data.filters, reserveDate: data.issuedAt})
                setTargetTargets({ member: true, nonMember: data.isSendMoment })
            })
        }

    }, [query]);

    // 단가표 조회 — 마운트 1회. 정가표(billing_catalog) 기준 부가세 포함 단가.
    useEffect(() => {
        getMessagePrices().then(setMessagePrices)
    }, []);

    // --- [실시간 유효성 검사 로직] ---
    useEffect(() => {
        if (step === 3) {
            // 🔴 단가를 하드코딩하지 않는다 — 정산이 쓰는 정가표(billing_catalog)와 갈라진다.
            //    종전 22 / 24.2 / 27.5 는 실제 청구보다 15~19% 낮았다(2026-08-28 실측).
            //    서버가 정가표 기준 부가세 포함 단가를 내려준다(`GET /volatility-message/message-prices`).
            //    아직 못 받았으면(빈 객체) 0 원으로 두고 표시하지 않는다 —
            //    **틀린 금액을 보여주는 것보다 안 보여주는 편이 낫다.**
            const memberUnit = messagePrices[msgType] ?? messagePrices['FI'] ?? 0
            const nonMemberUnit = messagePrices['MOMENT'] ?? 0
            setCost(((memberCount * memberUnit) + (nonMemberCount * nonMemberUnit)) | 0);
            setIsValid(true);
            setValidationMsg("");
            return;
        } else if (step !== 2) {
            setIsValid(true);
            setValidationMsg("");
            return;
        }

        if (!creatorData) {
            setIsValid(false);
            return;
        }

        // 검증 규칙은 validateMessageContent(파일 상단, 순수 함수) 한 곳에만 둔다.
        // 여기서는 기존 부수효과(쿠폰 버튼이면 비회원 발송 차단)만 넘겨준다.
        const error = validateMessageContent(msgType, creatorData, {
            onCouponPresence: (existsCoupon) => setIsNotSendMoment(existsCoupon),
            onCouponButtons: () => setTargetTargets({...targetTargets, nonMember: false}),
        });

        setValidationMsg(error?.message || "");
        setInvalidField(error?.field ?? null);
        setIsValid(!error);

    }, [step, msgType, creatorData, memberCount, nonMemberCount, messagePrices]);


    // Navigation Handlers
    const goNext = () => {
        if (step === 1) {
            if (!msgType) return alert('메시지 타입을 선택해주세요.');
            setStep(step + 1);
        } else if (step === 2) {
            if (isValid) setStep(step + 1);
        }
    };

    const goPrev = () => {
        if (step > 1) setStep(step - 1);
    };

    const processButtons = (rawButtons) => {
        const result = { buttons: [], coupon: null };
        if (!rawButtons || rawButtons.length === 0) return result;

        const normalButtons = [];
        let couponData = null;

        rawButtons.forEach(btn => {
            if (btn.type === 'COUPON') {
                if (!couponData) {
                    couponData = {
                        title: btn.name,
                        description: btn.description || "",
                        urlMobile: btn.urlMobile,
                        benefitId: btn.benefitId,
                        benefitExpiredDay: btn.benefitExpiredDay,
                        benefitExpiredDate: btn.benefitExpiredDate
                    };
                }
            } else {
                normalButtons.push({
                    name: btn.name,
                    type: 'WL',
                    urlMobile: btn.urlMobile
                });
            }
        });

        result.buttons = normalButtons.length > 0 ? normalButtons : null;
        result.coupon = couponData;
        return result;
    };

    const transformToMessageData = () => {
        if (!creatorData) return null;

        // 예약 시간 포맷팅 (YYYY-MM-DD HH:mm:ss)
        let formattedReservedTime = null;
        if (segmentData?.reserveDate?.date) {
            const { date, hour, min } = segmentData.reserveDate;
            formattedReservedTime = `${date} ${hour}:${min}`;
        }

        const messageData = {
            type: '',
            adult: 'N',
            reservedTime: formattedReservedTime,
            targetType: targetTargets.member ? 'filter' : 'moment',
            isNonMember: targetTargets.nonMember,
            filters: segmentData?.selectedFilters,
            content: null,
            image: null,
            header: null,
            items: null,
            carousels: null,
            buttons: null,
            coupon: null
        };

        switch (msgType) {
            case 'FI':
                messageData.type = msgType
                messageData.content = creatorData.content;
                if (creatorData.image) messageData.image = { imgUrl: creatorData.image.imgUrl };
                const baseBtns = processButtons(creatorData.buttons);
                messageData.buttons = baseBtns.buttons;
                messageData.coupon = baseBtns.coupon;
                break;
            case 'FW':
                messageData.type = msgType
                messageData.content = creatorData.content;
                if (creatorData.image) messageData.image = { imgUrl: creatorData.image.imgUrl };
                const wideBtns = processButtons(creatorData.buttons);
                messageData.buttons = wideBtns.buttons;
                messageData.coupon = wideBtns.coupon;
                break;
            case 'FL':
                messageData.type = msgType
                messageData.header = creatorData.header;
                messageData.items = creatorData.items.map(item => ({
                    title: item.title,
                    imgUrl: item.imgUrl,
                    urlMobile: item.urlMobile,
                }));
                const listBtns = processButtons(creatorData.buttons);
                messageData.buttons = listBtns.buttons;
                messageData.coupon = listBtns.coupon;
                break;
            case 'FC':
                messageData.type = msgType
                messageData.carousels = creatorData.carousels.map(feed => {
                    const feedBtns = processButtons(feed.attachment.buttons);
                    return {
                        header: feed.header,
                        message: feed.message,
                        attachment: {
                            image: feed.attachment.image ? { imgUrl: feed.attachment.image.imgUrl } : null,
                            buttons: feedBtns.buttons,
                            coupon: feedBtns.coupon
                        }
                    };
                });
                break;
            case 'FP':
                messageData.type = msgType
                messageData.header = creatorData.header;
                messageData.content = creatorData.content;
                messageData.video = creatorData.video;
                break;
            default: break;
        }
        return messageData;
    };


    const handleTemp = () => {
        const message = transformToMessageData();

        saveMessage(id, "TEMPORARY", campaignName, message).then(({data}) => {
            if (id == null) {
                window.location.href = window.location.href + `?id=${data}`
            }
            alert("저장 되었습니다.")
        })
    };

    const handleSend = () => {
        if (msgType === "FI" && !creatorData.image) {
            return alert("이미지를 첨부 해주세요.")
        }
        // 예약 확정 = 실제 발송 확정. 임시저장(handleTemp)은 막지 않지만 여기서는 필수 항목을 다시 확인한다.
        // step 3 에서는 isValid 가 무조건 true 라 이 검사가 없으면 빈 항목이 예약되고 발송 시점에 조용히 실패한다.
        const contentError = validateMessageContent(msgType, creatorData);
        if (contentError) {
            goBackToContentStep(contentError);
            return alert(`필수 항목을 모두 입력해야 발송을 예약할 수 있습니다.\n\n· ${contentError.message}`);
        }
        // 비회원 친구(모먼트)는 요구 항목이 더 많다 — 부족하면 발송 시점에 조용히 실패한다.
        if (targetTargets.nonMember) {
            const momentError = validateMomentContent(msgType, creatorData);
            if (momentError) {
                if (momentError.field) goBackToContentStep(momentError);
                return alert(`비회원 친구에게 발송하려면 아래 항목이 필요합니다.\n\n· ${momentError.message}`);
            }
        }
        if (!segmentData?.reserveDate?.date) {
            return alert('발송 예약 일시를 설정해주세요.');
        }

        const { date, hour, min } = segmentData.reserveDate;

        const reservedDateTime = new Date(`${date}T${hour}:${min}:00`);
        const now = new Date();

        if (reservedDateTime < now) {
            return alert('예약 시간은 현재 시간보다 미래여야 합니다.\n올바른 시간을 선택해주세요.');
        }

        setIsConfirmModalOpen(true);
    };

    // [New] 실제 전송 로직 (모달에서 실행)
    const executeSend = () => {
        const message = transformToMessageData();

        // 랜딩 URL 스킴 검증(프론트 1차). 스킴 누락 시 발송 차단(백엔드도 400 으로 재검증).
        const urlErrors = collectMessageUrlErrors(message);
        if (urlErrors.length > 0) {
            setIsConfirmModalOpen(false);
            setValidationMsg(urlErrors[0]);
            return alert(urlErrors[0]);
        }

        saveMessage(id, "RESERVED", campaignName, message).then(() => {
            alert('메시지 전송 예약이 완료되었습니다.');
            window.location.href = "/volatility-message"
        })
    };

    return (
        <div className="bg-gray-50 flex flex-col font-sans overflow-hidden" style={{height: "calc(100vh - 76px)"}} >

            {/* Main Content Area (Scrollable) */}
            <div className="flex-1 w-full max-w-7xl mx-auto py-10 px-4 overflow-y-auto">
                {/* 예약 건 안내 — 이 화면의 저장은 상태를 임시 저장(또는 새 예약)으로 다시 쓴다. 모르고 저장하면 예약이 풀린다. */}
                {loadedStatus === "RESERVED" && (
                    <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                        <span className="font-semibold">이 메시지는 발송 예약 상태입니다.</span>{" "}
                        내용을 바꿔 임시 저장하면 예약이 취소되고, 발송 설정에서 다시 예약해야 발송됩니다.
                    </div>
                )}
                {/* Step Indicator */}
                <div className="flex justify-center mb-10">
                    <div className="flex items-center space-x-4">
                        {['타입 선택', '메시지 제작', '발송 설정'].map((label, idx) => {
                            const num = idx + 1;
                            const isActive = step === num;
                            const isPast = step > num;
                            return (
                                <div key={num} className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm border-2 
                                        ${isActive ? 'bg-indigo-600 border-indigo-600 text-white' :
                                        isPast ? 'bg-indigo-100 border-indigo-100 text-indigo-600' : 'bg-white border-gray-300 text-gray-400'}
                                    `}>
                                        {isPast ? <Check size={16}/> : num}
                                    </div>
                                    <span className={`ml-2 text-sm font-medium ${isActive ? 'text-gray-900' : 'text-gray-400'}`}>{label}</span>
                                    {idx < 2 && <div className="w-10 h-0.5 bg-gray-200 mx-4"></div>}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Content */}
                <div className="mb-10">
                    {step === 1 && <OneTimeMessageSelection id={id} selectedType={msgType} campaignName={campaignName} onCampaignName={setCampaignName} onSelect={handleTypeSelect} />}
                    {step === 2 && (
                        <div className="flex flex-col items-center">
                            {!isValid && validationMsg && (
                                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center shadow-sm animate-fadeIn max-w-[400px] w-full">
                                    <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                                    <span className="text-sm font-medium">{validationMsg}</span>
                                </div>
                            )}
                            <OneTimeMessageCreator msgType={msgType} onUpdate={setCreatorData} initialData={creatorData}
                                                   isNonMember={targetTargets.nonMember}
                                                   highlightField={invalidField} />
                        </div>
                    )}
                    {step === 3 && (
                        <OneTimeMessageSegment
                            isNotSendMoment={isNotSendMoment}
                            targets={targetTargets} setTargets={(targets) => {
                                if (msgType === "FI") {
                                    if (targets.nonMember === false ||
                                        window.confirm("비회원 친구 전송 시 본문 내용이 300자로 제한됩니다.\n진행하시겠습니까?")
                                    ) {
                                        setTargetTargets(targets)
                                        setStep(2)
                                    }
                                } else {
                                    setTargetTargets(targets)
                                }
                        }}
                            memberCount={memberCount} nonMemberCount={nonMemberCount}
                            totalCount={totalCount} cost={cost}
                            initialData={segmentData} onUpdate={setSegmentData}
                            setMemberCount={setMemberCount}
                            setNonMemberCount={setNonMemberCount}
                            onTestSend={handleOpenTestModal}
                            // 비회원 친구를 켜기 전에 모먼트 요건을 미리 알려준다(쿠폰 첨부·미지원 유형 등).
                            momentBlockReason={validateMomentContent(msgType, creatorData)?.message ?? null}
                        />
                    )}
                </div>
            </div>

            {/* Navigation Footer (Fixed at bottom via Flex layout) */}
            <div className="w-full bg-white border-t border-gray-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-50">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <button onClick={goPrev} disabled={step === 1} className="px-6 py-3 rounded-xl border border-gray-300 text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">
                        이전
                    </button>

                    <div className="text-sm text-gray-500 hidden md:block">
                        {step === 1 && '메시지 유형을 선택해주세요.'}
                        {step === 2 && '메시지 내용과 버튼을 설정해주세요.'}
                        {step === 3 && '최종 발송 대상과 비용을 확인하세요.'}
                    </div>
                    <div className="flex gap-4">
                        <button onClick={handleTemp}
                                disabled={step === 1}
                                className={`px-8 py-3 rounded-xl font-bold shadow-md transform transition flex items-center
                               ${step === 1 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-500 text-white hover:bg-gray-700 active:scale-95'}
                            `}
                        >
                            임시저장
                        </button>

                        {step < 3 ? (
                            <button onClick={goNext} disabled={step === 2 && !isValid}
                                    className={`px-8 py-3 rounded-xl font-bold shadow-md transform transition flex items-center
                                ${step === 2 && !isValid ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95'}
                            `}
                            >
                                다음 <ChevronRight size={18} className="ml-1"/>
                            </button>
                        ) : (
                            <button
                                onClick={handleSend}
                                className="px-8 py-3 rounded-xl bg-yellow-400 text-black font-bold hover:bg-yellow-500 shadow-lg transform transition hover:-translate-y-1 active:scale-95 flex items-center"
                            >
                                메시지 예약하기
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <TestSendModal
                isOpen={isTestModalOpen}
                onClose={() => setIsTestModalOpen(false)}
                onSend={executeTestSend}
            />

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={executeSend}
                data={{ totalCount, cost, reserveDate: segmentData?.reserveDate }}
            />
        </div>
    );
};

export default OneTimeMessagePage;